import React from "react";
import styled from "styled-components/macro";
import { Box, Button } from "@mui/material";
import DialogWindow from "components/other/DialogWindow";
import { Trans } from "react-i18next";
import { useSelector } from "react-redux";
import { selectTheme } from "../../../components/ReduxProvider";

const Header = styled.section`
  padding: 15px;
  margin-top: 5px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;
const Body = styled.section`
  padding: 0 15px;
  text-align: left;
`;
const Title = styled.h1`
  font-family: "Noto Sans", sans-serif;
  text-align: center;
  font-size: 20px;
  font-weight: 400;
  margin: 10px 0;
`;
const HeaderImg = styled.img`
  width: 100%;
  margin: 5px auto 10px;
`;
const BadgesRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 20px 0;
`;
const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 999px;
  color: ${({ theme }) => theme.duo.color.text};
  font-size: 12px;
  line-height: 1;
  white-space: nowrap;
`;
const BadgeDashed = styled(Badge)`
  border: 1px dashed ${({ theme }) => theme.duo.palette.greyDark};
  opacity: 0.9;
`;
const BadgeSolid = styled(Badge)`
  border: 1px solid ${({ theme }) => theme.duo.palette.greyDark};
`;
const List = styled.ul`
  list-style: none;
  padding: 0;
  margin: 8px 0 0;
`;
const Item = styled.li`
  display: grid;
  grid-template-columns: 28px 1fr;
  column-gap: 10px;
  align-items: start;
  margin: 12px 0;
`;
const Num = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.duo.color.primary};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.duo.color.primary};
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
  font-size: 14px;
  margin-top: -2px;
`;
const ItemTitle = styled.div`
  font-weight: ${({ theme }) => theme.duo.fontWeight.w700};
`;
const ItemText = styled.div`
  color: ${({ theme }) => theme.duo.palette.greyDark};
  line-height: 1.3em;
`;

type HowLearningWorksDialogProps = {
  open: boolean;
  onClose: (e?: any) => void;
};

const HowLearningWorksDialog: React.FC<HowLearningWorksDialogProps> = ({ open, onClose }) => {
  const theme = useSelector(selectTheme);
  const headerImage =
    theme === "dark"
      ? "https://cdn.duocards.com/img/how-learning-works-dark.png"
      : "https://cdn.duocards.com/img/how-learning-works.png";
  return (
    <DialogWindow
      open={open}
      onClose={onClose}
      wide
      title={
        <Title>
          <Trans>How learning works</Trans>
        </Title>
      }
      bottom={
        <Box p={1.5} width="100%">
          <Button onClick={onClose} fullWidth variant="contained" color="primary">
            <Trans>Got it</Trans>
          </Button>
        </Box>
      }
    >
      <Header>
        <BadgesRow>
          <BadgeDashed>
            <Trans>Practiced</Trans>
          </BadgeDashed>
          <BadgeSolid>
            <span role="img" aria-label="party emoji">
              🎉
            </span>
            <Trans>Learned</Trans>
          </BadgeSolid>
        </BadgesRow>
        <HeaderImg src={headerImage} alt="learning visualisation" />
      </Header>
      <Body>
        <List>
          <Item>
            <Num>1</Num>
            <div>
              <ItemTitle>
                <Trans>To learn</Trans>
              </ItemTitle>
              <ItemText>
                <Trans>Cards ready to study now.</Trans>
              </ItemText>
            </div>
          </Item>
          <Item>
            <Num>2</Num>
            <div>
              <ItemTitle>
                <Trans>Practiced</Trans>
              </ItemTitle>
              <ItemText>
                <Trans>Cards you are starting to know. You will see them less often.</Trans>
              </ItemText>
            </div>
          </Item>
          <Item>
            <Num>3</Num>
            <div>
              <ItemTitle>
                <Trans>Learned</Trans>
              </ItemTitle>
              <ItemText>
                <Trans>Cards you know well. We will show them again much later to refresh your memory.</Trans>
              </ItemText>
            </div>
          </Item>
        </List>
      </Body>
    </DialogWindow>
  );
};

export default HowLearningWorksDialog;
